import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  MessageSquare, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Bot, 
  Zap,
  ArrowRight
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState({
    totalConversations: 0,
    activeConversations: 0,
    totalProducts: 0,
    activeCampaigns: 0
  });
  const [loading, setLoading] = useState(true);

  // Dados do gráfico (serão carregados do backend futuramente)
  const [chartData, setChartData] = useState([
    { name: 'Jan', conversas: 0, vendas: 0 },
    { name: 'Fev', conversas: 0, vendas: 0 },
    { name: 'Mar', conversas: 0, vendas: 0 },
    { name: 'Abr', conversas: 0, vendas: 0 },
    { name: 'Mai', conversas: 0, vendas: 0 },
    { name: 'Jun', conversas: 0, vendas: 0 },
  ]);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Carregar estatísticas reais do backend
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/stats`);
        
        if (response.ok) {
          const data = await response.json();
          setStats({
            totalConversations: data.totalConversations || 0,
            activeConversations: data.activeConversations || 0,
            totalProducts: data.totalProducts || 0,
            activeCampaigns: data.activeCampaigns || 0
          });
        } else {
          // Fallback para dados padrão se não conseguir carregar
          setStats({
            totalConversations: 0,
            activeConversations: 0,
            totalProducts: 0,
            activeCampaigns: 0
          });
        }
      } catch (error) {
        console.error('Erro ao carregar dados do dashboard:', error);
        // Fallback para dados padrão em caso de erro
        setStats({
          totalConversations: 0,
          activeConversations: 0,
          totalProducts: 0,
          activeCampaigns: 0
        });
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user?.uid]);



  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header do Dashboard */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-secondary-900 font-display">
                Olá, {user?.businessName}!
              </h1>
              <p className="text-secondary-600 mt-1">
                Aqui está um resumo do seu negócio hoje
              </p>
            </div>

          </div>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-lg">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total de Conversas</p>
                <p className="text-2xl font-bold text-secondary-900">{stats.totalConversations}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Conversas Ativas</p>
                <p className="text-2xl font-bold text-secondary-900">{stats.activeConversations}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Bot className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Produtos</p>
                <p className="text-2xl font-bold text-secondary-900">{stats.totalProducts}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-primary-100 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Campanhas Ativas</p>
                <p className="text-2xl font-bold text-secondary-900">{stats.activeCampaigns}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Gráfico e Ações Rápidas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Gráfico */}
          <div className="lg:col-span-2 card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              Conversas e Vendas (Últimos 6 meses)
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="conversas" fill="#3b82f6" name="Conversas" />
                <Bar dataKey="vendas" fill="#f97316" name="Vendas" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Ações Rápidas */}
          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              Ações Rápidas
            </h3>
            <div className="space-y-3">
              <Link
                to="/products"
                className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Bot className="w-5 h-5 text-primary-600" />
                  <span className="font-medium text-secondary-900">Gerenciar Produtos</span>
                </div>
                <ArrowRight className="w-4 h-4 text-secondary-400" />
              </Link>

              <Link
                to="/conversations"
                className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-secondary-900">Ver Conversas</span>
                </div>
                <ArrowRight className="w-4 h-4 text-secondary-400" />
              </Link>

              <Link
                to="/campaigns"
                className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-secondary-900">Campanhas</span>
                </div>
                <ArrowRight className="w-4 h-4 text-secondary-400" />
              </Link>
            </div>
          </div>
        </div>

        {/* Status do Assistente */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-secondary-900">
              Status do Assistente IA
            </h3>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-green-600 font-medium">Ativo</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                <Zap className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm font-medium text-secondary-900">Tempo de Resposta</p>
              <p className="text-lg font-bold text-green-600">
                {stats.responseTime || '< 1s'}
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-secondary-900">Mensagens Hoje</p>
              <p className="text-lg font-bold text-blue-600">
                {stats.messagesToday || 0}
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-sm font-medium text-secondary-900">Conversões Hoje</p>
              <p className="text-lg font-bold text-purple-600">
                {stats.conversionsToday || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;

